import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

export interface TenancyRecipient {
  id: string;
  name: string;
}

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (subject: string, message: string, tenancyId?: string) => void;
  recipientName: string; // Property name
  tenancies?: TenancyRecipient[];
  initialSubject?: string; // Auto-fill support
  isSending?: boolean;
}

const TEMPLATES = [
  {
    id: 'reminder',
    label: 'Payment Reminder',
    subject: 'Action Required: Outstanding Rent Payment',
    body: 'Hi,\n\nI hope you are well.\n\nThis is a friendly reminder that we have not yet received your rent payment for this month. Please arrange payment at your earliest convenience to avoid any arrears accruing.\n\nBest regards,'
  },
  {
    id: 'maintenance',
    label: 'Maintenance Check',
    subject: 'Upcoming Maintenance Visit',
    body: 'Hi,\n\nWe need to schedule a brief maintenance check for the property. Please let me know what dates/times work best for you this week.\n\nThanks,'
  },
  {
    id: 'inspection',
    label: 'Inspection Notice',
    subject: 'Notice of Routine Property Inspection',
    body: 'Hi,\n\nThis is to inform you that we will be conducting a routine property inspection on [Date] between [Time].\n\nPlease let us know if this time is suitable or if we need to reschedule.\n\nThanks,'
  },
  {
    id: 'renewal',
    label: 'Lease Renewal',
    subject: 'Upcoming Lease Expiry & Renewal Options',
    body: 'Hi,\n\nYour current lease is due to expire soon. We would like to know if you are interested in renewing the tenancy for another term.\n\nPlease let us know your intentions at your earliest convenience.\n\nBest regards,'
  },
  {
    id: 'contractor',
    label: 'Contractor Visit',
    subject: 'Scheduled Contractor Visit',
    body: 'Hi,\n\nA contractor will be visiting the property on [Date] to address [Issue].\n\nPlease confirm if someone will be home or if you grant permission for access.\n\nThanks,'
  },
  {
    id: 'general',
    label: 'General Update',
    subject: 'Update regarding your tenancy',
    body: 'Hi,\n\nJust writing to let you know...'
  }
];

const BroadcastModal: React.FC<MessageModalProps> = ({
  isOpen,
  onClose,
  onSend,
  recipientName,
  tenancies = [],
  initialSubject = '',
  isSending = false
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [messageAll, setMessageAll] = useState(true);
  const [selectedTenancyId, setSelectedTenancyId] = useState<string>('');

  // Reset or pre-fill when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubject(initialSubject);
      setMessageAll(true);
      if (tenancies.length > 0) {
        setSelectedTenancyId(tenancies[0].id);
      }
      // If a specific subject is passed (like "Payment Reminder"), try to find a matching template body
      if (initialSubject.toLowerCase().includes('reminder')) {
        const template = TEMPLATES.find((t) => t.id === 'reminder');
        if (template) setMessage(template.body);
      } else {
        setMessage('');
      }
    }
  }, [isOpen, initialSubject, tenancies]);

  if (!isOpen) return null;

  const applyTemplate = (templateId: string) => {
    const template = TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setMessage(template.body);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="relative w-full max-w-lg rounded-3xl bg-white text-left shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between items-center border-b border-gray-100">
          <h3
            className="text-lg leading-6 font-medium text-gray-900"
            id="modal-title"
          >
            Message Tenants
          </h3>
          <button
            onClick={onClose}
            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          {/* Context: Who are we messaging? */}
          <div className="mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-800 font-medium">
                Property: {recipientName}
              </span>
            </div>

            {tenancies.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    id="message-all"
                    type="checkbox"
                    checked={messageAll}
                    onChange={(e) => setMessageAll(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="message-all"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Message all primary tenants
                  </label>
                </div>

                <div
                  className={`transition-opacity duration-200 ${
                    messageAll ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <label
                    htmlFor="recipient-select"
                    className="block text-xs font-medium text-blue-700 mb-1"
                  >
                    Select Tenant:
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserGroupIcon className="h-4 w-4 text-blue-400" />
                    </div>
                    <select
                      id="recipient-select"
                      value={selectedTenancyId}
                      onChange={(e) => setSelectedTenancyId(e.target.value)}
                      disabled={messageAll}
                      className="block w-full pl-9 pr-10 py-2 text-sm border-blue-200 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100"
                    >
                      {tenancies.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-blue-600 mt-2 flex items-center">
                <UserGroupIcon className="h-4 w-4 mr-2" />
                No active tenancies found.
              </div>
            )}
          </div>

          {tenancies.length > 0 ? (
            <>
              {/* Quick Templates Chips */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                  <SparklesIcon className="w-3 h-3" /> Quick Templates
                </label>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t.id)}
                      type="button"
                      className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Inputs */}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Subject
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="subject"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                      placeholder="e.g. Important Update"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Message
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="message"
                      rows={6}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                      placeholder={`Write your message to ${recipientName}...`}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-10">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No Tenants
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Add tenants to this property to send messages.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          {tenancies.length > 0 && (
            <button
              type="button"
              disabled={isSending || !message.trim()}
              onClick={() =>
                onSend(
                  subject,
                  message,
                  messageAll ? undefined : selectedTenancyId
                )
              }
              className="mt-3 w-full inline-flex justify-center items-center rounded-md border  bg-indigo-600  border-purple-300 shadow-sm px-4 py-2 text-white text-base font-medium hover:bg-indigo-700 focus:outline-none sm:mt-0 sm:w-40 sm:text-sm"
            >
              {isSending ? (
                'Sending...'
              ) : (
                <>
                  <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mr-3 mt-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-40 sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastModal;
